import Tooltip from '@material-ui/core/Tooltip';
import WarningIcon from '@material-ui/icons/Warning';
import { Typography } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { APPLICATION_PATHS } from '../application-core/constants';
import Errored from '../core/Errored';
import Loading from '../core/Loading';
import ResourceLink from '../core/ResourceLink';
import TitledPanel from '../core/TitledPanel';
import GS from '../grafsync/grafsync';
import ConceptLink from '../vocabulary/core/ConceptLink';
import SchemeChip from '../vocabulary/core/SchemeChip';
import ActionText from './ActionText';
import { globalStyles } from './style/gstheme';
import { CLASS_LOAD_OPTIONS, CONCEPT_LOAD_OPTIONS } from './utils/constants';
import { isInOntology, makeCancelable, sortResources } from './utils/utils';
import withGS from './withGShoc';

const styles = theme => {
    return Object.assign({
        treeNumber: {
            height: 30,
            backgroundColor: '#f5f5f5',
            maxWidth: 28,
            marginTop: -30,
            position: 'relative',
            textAlign: 'right'
        }
    }, globalStyles);
};

class Positioning extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isErrored: null,
            isLoading: true,
            tree: {},
            uniqueParents: 0,
            occurrences: 0,
            maximumDepth: 0
        };
        this.cancellable = null;
    }

    componentDidMount() {
        this.getPositioning();
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    loadData(iriList, options) {
        this.cancellable = makeCancelable(GS.loadData(iriList, options, this.props.graph));
        return this.cancellable;
    }

    doPath(payload) {
        this.cancellable = makeCancelable(GS.path(payload));
        return this.cancellable;
    }

    createTree = (nodes, data, occurrences = [], path = [], level = 0) => {
        let childTree = {};
        let newLevel = level;
        let leafCount = 0;
        for (let node of nodes) {
            // Need to stop cyclical references
            if (!path.includes(node) && !path.includes(this.props.id)) {
                const children = data.filter(pd => pd.parent === node).map(pd => pd.child);
                const newPath = [...path, node];
                const results = this.createTree(children, data, occurrences, newPath, level + 1);
                leafCount += results.leafCount;
                if (node === this.props.id) leafCount++;
                childTree[node] = {
                    leafCount: results.leafCount,
                    path,
                    results: results.tree
                };
                if (results.level > newLevel) newLevel = results.level;
            }
        }
        return {
            tree: childTree,
            level: newLevel,
            leafCount
        };
    };

    /* Returns an array of IRIs that is the path to the found concept */
    getPositioning = async () => {
        try {
            const positioningData = await this.doPath({
                graph: this.props.graph,
                source: this.props.id,
                path: this.props.path
            }).promise;
            // Load everything in the tree
            const loadOptions = this.props.displayType === "Class" ?
                CLASS_LOAD_OPTIONS
                :
                CONCEPT_LOAD_OPTIONS;
            let additionalTopNodes = [];
            if (this.props.displayType === "Concept") {
                const childConcepts = positioningData.results.map(x => x.child);
                const topConcepts = positioningData.results.map(x => x.parent);
                const allConcepts = [...new Set([...childConcepts, ...topConcepts])];
                await this.loadData(allConcepts, loadOptions).promise;
                for (let tc of allConcepts) {
                    const conceptData = GS.data(tc, this.props.graph, false);
                    if (conceptData) {
                        const inScheme = conceptData.inScheme;
                        if (inScheme && inScheme.length > 0) {
                            for (let scheme of inScheme) {
                                await this.loadData([scheme], {}).promise;
                                const data = GS.data(scheme, this.props.graph, false);
                                if (data && data.hasTopConcept && data.hasTopConcept.includes(tc)) {
                                    additionalTopNodes.push({
                                        child: tc,
                                        parent: scheme
                                    });
                                }
                            }
                        }
                    }
                }
            }
            const allNodes = [...positioningData.results, ...additionalTopNodes];
            const topNodes = allNodes
                .filter(pd => !allNodes.some(x => x.child === pd.parent))
                .map(tn => tn.parent);
            const childNodes = allNodes.map(pd => pd.child);
            const uniqueNodes = [...new Set([...topNodes, ...childNodes])];
            await this.loadData(uniqueNodes, loadOptions).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            const uniqueParents = new Set(allNodes.filter(pd => pd.child === this.props.id));
            let occurrenceList = [];
            const results = this.createTree(topNodes, allNodes, occurrenceList);
            Object.values(results.tree).forEach(result => occurrenceList.push(result.leafCount));
            const occurrences = occurrenceList.length > 0 ?
                occurrenceList.reduce(function (a, b) { return a + b; })
                :
                0;
            this.setState({
                isLoading: false,
                tree: results.tree,
                uniqueParentsSize: uniqueParents.size,
                maximumDepth: results.level,
                occurrences
            });
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ isErrored: e });
        }
    };

    jumpToStructurePoint = (path, node) => {
        let navPath = [...path];
        navPath.shift();
        this.props.onFoundResource(node, navPath);
    };

    isNotLocalClass = (data) => {
        if (!data) return false;
        const ontology = data.visibility || data.isDefinedBy;
        const notInOntology = !ontology
            || (!(data.isDefinedBy && isInOntology(data.id, data.isDefinedBy, this.props.componentIRI))
                && data.visibility !== this.props.componentIRI);
        return notInOntology;
    };

    buildOntologyLink = (data) => {
        const notInOntology = this.isNotLocalClass(data);
        if (!notInOntology) return null;
        const ontology = data.visibility || data.isDefinedBy;
        let link = APPLICATION_PATHS.ONTOLOGY_MANAGER + '/project/ontology#project=' +
            this.props.projectIRI + "&ontology=" + ontology + "&view=class&class=" + data.id;
        return link;
    };

    buildConceptLink = (component, iri) => {
        let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' +
            this.props.projectIRI + "&taxonomy=" + component + "&view=concept&concept=" + iri;
        return link;
    };

    buildSchemeLink = (component) => {
        let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project=' +
            this.props.projectIRI + "&taxonomy=" + component;
        return link;
    };

    createTreeStructure = (tree, linkablePaths, level = 0, branchComponent) => {
        if ((Object.keys(tree)).length === 0) return null;
        const langOption = this.props.viewLang && this.props.viewLang !== this.props.defaultLang ? [this.props.defaultLang, this.props.viewLang] : this.props.defaultLang;
        const labelProp = this.props.displayType === "Class" ? "label" : "prefLabel";
        const sortedStructure = sortResources(Object.keys(tree), this.props.graph, labelProp, langOption);
        const output = sortedStructure.map((keyName, index) => {
            GS.handle("create-positioning");
            const nodeData = GS.data(keyName, this.props.graph, false);
            const isLinkableExternal = keyName === "http://www.w3.org/2002/07/owl#Thing";
            const inComponent = branchComponent ?
                branchComponent
                :
                this.props.components.includes(keyName) ? keyName : "";
            const isNotLocal = this.props.displayType === "Class" ?
                this.isNotLocalClass(nodeData)
                :
                inComponent !== this.props.componentIRI;
            const isLinkable = !linkablePaths || linkablePaths.includes(keyName);
            const newLinkablePaths = isLinkable && (isLinkableExternal || !isNotLocal) ? null : [];
            const nodePath = [...tree[keyName].path];
            const nodes = Object.entries(tree[keyName].results);
            return <div
                style={{
                    marginLeft: 24,
                    ...(level > 0) && {
                        borderLeft: 'dashed 1px #c0c0c0'
                    },
                    ...(level === 0 && index === sortedStructure.length) && {
                        marginBottom: 24
                    }
                }}
                key={index}>
                <div
                    data-test={"tree-index-" + index + "-" + level}
                    style={
                        { paddingLeft: level === 0 ? 0 : 12 }
                    }
                    key={index}>
                    <div data-test="tree-entry">
                        {this.props.displayType === "Class" ?
                            <ResourceLink
                                id={keyName}
                                compact={true}
                                dataTest={nodes.length > 0 || keyName !== this.props.id ? "grey-chip" : "focus-chip"}
                                highlight={nodes.length > 0 || keyName !== this.props.id}
                                graph={this.props.graph}
                                clickable={isLinkable || isNotLocal}
                                projectGraph={this.props.graph}
                                context={this.props.componentIRI}
                                contextProperty="isDefinedBy"
                                handleNotAvailable={true}
                                link={isNotLocal && !isLinkableExternal ? this.buildOntologyLink : () => { }}
                                onClick={!isNotLocal && !isLinkableExternal ?
                                    () => this.jumpToStructurePoint(nodePath, keyName)
                                    :
                                    null}
                                loadOptions={{ language: langOption }}
                                isReadOnly={true}
                                viewLang={this.props.viewLang}
                                defaultLang={this.props.defaultLang} />
                            :
                            this.props.components.includes(keyName) ?
                                <SchemeChip
                                    id={keyName}
                                    viewLang={this.props.viewLang}
                                    defaultLang={this.props.defaultLang}
                                    isReadOnly={true}
                                    highlight={true}
                                    handleNotAvailable={true}
                                    compact={true}
                                    graph={this.props.graph}
                                    property="inScheme"
                                    link={this.buildSchemeLink(keyName)} />
                                :
                                <ConceptLink
                                    highlight={nodes.length > 0 || keyName !== this.props.id}
                                    id={keyName}
                                    disabled={inComponent !== this.props.componentIRI}
                                    otherScheme={inComponent !== this.props.componentIRI}
                                    link={isNotLocal && !isLinkableExternal ? this.buildConceptLink(branchComponent, keyName) : null}
                                    onClick={!isNotLocal ?
                                        () => this.jumpToStructurePoint(nodePath, keyName)
                                        :
                                        null}
                                    compact={true}
                                    handleNotAvailable={true}
                                    graph={this.props.graph}
                                    propertyGraph={this.props.graph}
                                    isReadOnly={true}
                                    loadOptions={{ language: langOption }}
                                    viewLang={this.props.viewLang}
                                    defaultLang={this.props.defaultLang} />
                        }
                    </div>
                    {(nodes.length === 0 && keyName !== this.props.id) && <div
                        data-test="cyclical"
                        style={{
                            marginLeft: 36,
                            border: 'solid 1px #DD2C00',
                            borderRadius: 18,
                            padding: '2px 0px 0px 12px',
                            marginBottom: 6,
                            color: '#DD2C00'
                        }}>
                        <Typography variant="body2"><Tooltip title="There are cylical references below this level">
                            <WarningIcon data-test="warning" />
                        </Tooltip>
                            <span style={{ display: 'inline-block', marginLeft: 4, transform: 'translateY(-6px)' }}>Cyclical references</span>
                        </Typography>
                    </div>}
                    {this.createTreeStructure(tree[keyName].results, newLinkablePaths, level + 1, inComponent)}
                    {(keyName !== this.props.id) && nodes.length > 0 &&
                        <div className={this.props.classes.treeNumber} data-test={"tree-number-" + index + "-" + level}>
                            <Typography variant="caption">{level + 1}</Typography>
                        </div>
                    }
                </div>
            </div>;
        });
        return output;
    };

    render() {

        const { classes, data, displayType } = this.props;
        const { tree, uniqueParentsSize, occurrences, maximumDepth, isLoading, isErrored } = this.state;

        if (isErrored) return <Errored dataTest="bulk-error" data={isErrored} />;
        if (isLoading) return <div style={{ height: 280 }}><Loading offset="130px" /></div>;
        if (data._notAvailable) return <ActionText offset="30px">Positioning is not available.</ActionText>;

        let treeOutput;
        try {
            treeOutput = this.createTreeStructure(tree,
                displayType === "Class" ?
                    ["http://www.w3.org/2002/07/owl#Thing"]
                    :
                    null);
        }
        catch (e) {
            return <Errored dataTest="bulk-error" data={{ message: "Cannot generate positioning information" }} />;
        }

        return (
            <div style={{ marginTop: 10 }}>
                <Typography paragraph className={classes.mainHeading} style={{ marginTop: 10 }}>Positioning</Typography>
                <div style={{ marginBottom: 24, backgroundColor: '#f5f5f5', borderRadius: 4, padding: '24px 12px' }}>
                    {occurrences === 0 ?
                        <div style={{ height: 155 }}>
                            <ActionText offset="100px">This resource is only at the top of the hierarchy.</ActionText>
                        </div>
                        :
                        <Grid container spacing={2}>
                            <Grid item xs={12} lg={3}>
                                <TitledPanel dataTest="basic-statistics" fontSize="large" title="Hierarchy Statistics">
                                    <Typography className={classes.subSubHeading}>Occurrences</Typography>
                                    <Typography data-test="occurrence-count" className={classes.statistic}>{occurrences}</Typography>
                                    <Typography className={classes.subSubHeading}>Maximum Depth</Typography>
                                    <Typography data-test="maximum-depth" className={classes.statistic}>{maximumDepth}</Typography>
                                    <Typography className={classes.subSubHeading}>Unique Parents</Typography>
                                    <Typography data-test="unique-parents" className={classes.statistic}>{uniqueParentsSize}</Typography>
                                </TitledPanel>
                            </Grid>
                            <Grid item xs={12} lg={9} style={{ overflowX: 'scroll' }}>
                                <div data-test="tree-output" style={{ width: maximumDepth * 38 + 200 }}>
                                    {treeOutput}
                                </div>
                            </Grid>
                        </Grid>
                    }
                </div>
            </div>
        );
    }
}

Positioning.defaultProps = {
    displayType: "Concept"
};

Positioning.propTypes = {
    displayType: PropTypes.string,
    classes: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    onFoundResource: PropTypes.func,
    componentIRI: PropTypes.string,
    path: PropTypes.string.isRequired,
    projectIRI: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    graph: PropTypes.string.isRequired
};

export default withStyles(styles)(withGS(Positioning, ["ALL"], null, {}, true));